
import Vue from 'vue'
import { PinFeature, RECOVER_QUERY_ID, RECOVER_QUERY_VALUE } from '@/stores/pin-store'
import { mapper } from '@/stores/store'

export default Vue.extend({
  data() {
    return {
      // Enable preview box boolean
      withPreviewBox: true,
      isPinPortrait: true,
      pinBoxSize: 0,
      previewCircleSize: 0
    }
  },
  computed: {
    ...mapper(['uploading', 'previewBox']),
    ...mapper('pin', ['features']),
    ...mapper('user', ['isLoggedIn']),
    pin(): HTMLElement {
      return this.$refs.pin as HTMLElement
    },
  },
  watch: {
    'features.ears'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.earrings'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.head'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.skin_accessories'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.beard'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.mouth'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.eyes'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.eyebrows'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.hair'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.hands'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.accessories'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
    'features.effect'(feature: PinFeature, prevFeature: PinFeature) {
      this.checkFeatureChanges(feature, prevFeature)
    },
  },
  async mounted() {
    const pinContainer = await this.initializePin()
    this.pin.appendChild(pinContainer)

    // If recover mode try to load state from storage
    if (this.$route.query[RECOVER_QUERY_ID] === RECOVER_QUERY_VALUE) {
      this.recoverPinFromLocalStorage()
        .then(() => this.finishEditingDownload(true))
        .catch(() => this.randomizePin())
    } else {
      // Try to load saved app state
      this.loadAppStateFromLocalStorage()
        .catch(() => this.randomizePin())
    }

    if (this.withPreviewBox) {
      this.$root.$on('resize', this.onResize)
      this.onResize()
    }
  },
  beforeDestroy() {
    if (this.withPreviewBox) {
      this.$root.$off('resize', this.onResize)
    }
  },
  methods: {
    ...mapper(['finishEditingDownload', 'loadAppStateFromLocalStorage']),
    ...mapper('pin', [
      'initializePin',
      'recoverPinFromLocalStorage',
      'randomizePin',
      'removeFeatureElement',
      'addFeatureElement',
      'applyElementColor',
      'setPositionOf',
    ]),
    onResize() {
      const setResizeValues = () => {
        this.isPinPortrait = this.pin.clientWidth / this.pin.clientHeight > 1
        this.pinBoxSize = this.isPinPortrait ? this.pin.clientHeight : this.pin.clientWidth
        this.previewCircleSize = this.pinBoxSize * 1.08 // Slightly enlarge the preview circle to match the final generated image
      }

      // Set values immediately
      setResizeValues()

      // Set values after pin transition is completed to get correct sizes
      setTimeout(() => {
        setResizeValues()
      }, 200)
    },
    checkFeatureChanges(feature: PinFeature, prevFeature: PinFeature) {
      if (feature.selectedAsset !== prevFeature.selectedAsset) {
        this.onAssetChanged(feature)
      }
      if (feature.hidden !== prevFeature.hidden) {
        this.onHiddenChanged(feature)
      }
      if (feature.color !== prevFeature.color) {
        this.onColorChanged(feature, prevFeature)
      }
      if (
        feature.position.x !== prevFeature.position.x ||
        feature.position.y !== prevFeature.position.y ||
        feature.scale !== prevFeature.scale ||
        feature.rotation !== prevFeature.rotation
      ) {
        this.onPositionRotationChanged(feature)
      }
    },
    onAssetChanged(feature: PinFeature) {
      this.removeFeatureElement(feature)
      if (feature.selectedAsset !== -1) {
        this.addFeatureElement(feature)
      }
    },
    onHiddenChanged(feature: PinFeature) {
      feature.container.style.opacity = feature.hidden ? '0' : '1'
    },
    onColorChanged(feature: PinFeature, prevFeature: PinFeature) {
      this.applyElementColor({ feature, prevColor: prevFeature.color })
    },
    onPositionRotationChanged(feature: PinFeature) {
      let transform = ''
      transform += `translateX(${feature.position.x - feature.offset.x}px) `
      transform += `translateY(${feature.position.y - feature.offset.y}px) `
      transform += `rotate(${feature.rotation}deg) `
      transform += `scale(${feature.scale})`
      feature.container.style.transform = transform
      feature.container.style.transformOrigin = `${feature.offset.x}px ${feature.offset.y}px`
      feature.container.style.transformBox = `view-box`
    },
  },
})
